import { useMemo, useState } from "react";

import { Card, Empty, Money, Row, Rows, Scroller, Segmented, inputClass } from "../components/ui.js";
import {
  deriveLedger,
  deriveLedgerList,
  formatDate,
  formatMoney,
} from "../derive/reports.js";
import { ShareBar } from "../components/ShareBar.js";
import { navigate } from "../routes.js";
import { statementMessage } from "../share.js";
import { useApp } from "../state.js";

export function Ledgers() {
  const { snapshot, groups } = useApp();
  const [query, setQuery] = useState("");

  const rows = useMemo(
    () => (snapshot && groups ? deriveLedgerList(snapshot, groups) : []),
    [snapshot, groups],
  );

  const filtered = useMemo(() => {
    const needle = query.trim().toLowerCase();
    if (!needle) return rows;
    return rows.filter(
      (r) => r.name.toLowerCase().includes(needle) || r.group.toLowerCase().includes(needle),
    );
  }, [rows, query]);

  if (!snapshot) return <Empty>Nothing loaded.</Empty>;

  return (
    <div className="space-y-4">
      <input
        className={inputClass}
        placeholder="Filter ledgers"
        value={query}
        onChange={(e) => setQuery(e.target.value)}
      />
      <Card title={`${filtered.length} of ${rows.length}`}>
        {filtered.length === 0 ? (
          <Empty>No ledger matches.</Empty>
        ) : (
          <Rows>
            {filtered.slice(0, 300).map((row) => (
              <Row
                key={row.name}
                title={row.name}
                sub={row.group}
                // Debit balances are negative in the data; a debtor "owing" is
                // shown as a plain positive with a Dr marker.
                right={<Money amount={row.balance} />}
                rightSub={row.balance < 0 ? "Dr" : row.balance > 0 ? "Cr" : ""}
                onClick={() => navigate({ id: "ledger", param: row.name })}
              />
            ))}
          </Rows>
        )}
      </Card>
    </div>
  );
}

export function LedgerDetail({ name }: { name: string }) {
  const { snapshot, asOf } = useApp();
  const [order, setOrder] = useState<"newest" | "oldest">("newest");

  const statement = useMemo(
    () => (snapshot ? deriveLedger(snapshot, name) : null),
    [snapshot, name],
  );

  /*
   * The running balance is COMPUTED oldest-first — it has to be, each line's
   * balance is the one before it plus this entry. Only the DISPLAY is reversed.
   *
   * Newest-first is the default because on a phone the useful question is
   * "what happened lately", and a party with two years of history would
   * otherwise open on transactions from the start of the books. Each row still
   * shows the balance as it stood after that transaction, so the newest row
   * carries the current balance — which is what you want at the top anyway.
   */
  const lines = useMemo(() => {
    if (!statement) return [];
    return order === "newest" ? [...statement.lines].reverse() : statement.lines;
  }, [statement, order]);

  if (!statement) return <Empty>No ledger called “{name}” in this snapshot.</Empty>;

  return (
    <div className="space-y-4">
      <Card title={statement.ledger}>
        <div className="grid grid-cols-2 gap-3 text-sm">
          <div>
            <div className="text-xs uppercase text-ink-3">Opening</div>
            <Money amount={statement.opening} exact />
            <span className="ml-1 text-xs text-ink-3">{statement.opening < 0 ? "Dr" : "Cr"}</span>
          </div>
          <div>
            <div className="text-xs uppercase text-ink-3">Closing</div>
            <Money amount={statement.closing} exact />
            <span className="ml-1 text-xs text-ink-3">{statement.closing < 0 ? "Dr" : "Cr"}</span>
          </div>
        </div>
        {!statement.tiesOut && (
          // The running balance not landing on Tally's reported closing means
          // the snapshot's window starts after some transactions. Saying so
          // beats showing a column of numbers that quietly does not add up.
          <p className="mt-3 rounded-lg bg-bad-wash p-2 text-xs text-bad">
            The transactions below sum to {formatMoney(statement.computedClosing)}, not the closing
            balance above. The snapshot's date range probably starts after this ledger's first
            entries.
          </p>
        )}
      </Card>

      <ShareBar
        actions={[
          {
            label: "Send statement",
            build: () => ({
              title: `${statement.ledger} — statement`,
              body: statementMessage({
                company: snapshot?.company.name ?? "",
                party: statement.ledger,
                closing: statement.closing,
                asOf,
                lines: statement.lines.map((l) => ({
                  date: l.date,
                  voucherType: l.voucherType,
                  voucherNumber: l.voucherNumber,
                  amount: l.amount,
                  balance: l.balance,
                })),
              }),
            }),
          },
        ]}
        csv={[
          {
            label: "Export CSV",
            filename: `${statement.ledger.replace(/[^\w -]+/g, "")} statement`,
            build: () => [
              ["Date", "Voucher type", "Voucher no", "Particulars", "Debit", "Credit", "Balance"],
              // Oldest-first in the file regardless of the on-screen order: a
              // running balance column only makes sense in the order it was
              // computed, and a spreadsheet can be re-sorted, a balance cannot.
              ...statement.lines.map((l) => [
                l.date,
                l.voucherType,
                l.voucherNumber,
                l.counterparty,
                l.amount < 0 ? (-l.amount).toFixed(2) : "",
                l.amount > 0 ? l.amount.toFixed(2) : "",
                l.balance.toFixed(2),
              ]),
            ],
          },
        ]}
      />

      <Segmented
        value={order}
        onChange={setOrder}
        options={[
          { value: "newest", label: "Newest first" },
          { value: "oldest", label: "Oldest first" },
        ]}
      />

      <Card title={`${statement.lines.length} transactions`}>
        {statement.lines.length === 0 ? (
          <Empty>No transactions in this period.</Empty>
        ) : (
          <Scroller>
            <table className="w-full min-w-[34rem] text-sm">
              <thead>
                <tr className="text-left text-xs uppercase tracking-wide text-ink-3">
                  <th className="py-2 pr-3 font-medium">Date</th>
                  <th className="py-2 pr-3 font-medium">Particulars</th>
                  <th className="py-2 pr-3 text-right font-medium">Debit</th>
                  <th className="py-2 pr-3 text-right font-medium">Credit</th>
                  <th className="py-2 text-right font-medium">Balance</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-line">
                {lines.map((line, i) => (
                  <tr
                    key={`${line.voucherId}-${i}`}
                    className="cursor-pointer hover:bg-surface-2"
                    onClick={() => navigate({ id: "voucher", param: line.voucherId })}
                  >
                    <td className="py-2 pr-3 whitespace-nowrap text-ink-2">
                      {formatDate(line.date)}
                    </td>
                    <td className="py-2 pr-3">
                      <div className="truncate text-ink">{line.counterparty || line.voucherType}</div>
                      <div className="truncate text-xs text-ink-3">
                        {line.voucherType} {line.voucherNumber}
                      </div>
                    </td>
                    <td className="py-2 pr-3 text-right tabular">
                      {line.amount < 0 ? formatMoney(-line.amount) : ""}
                    </td>
                    <td className="py-2 pr-3 text-right tabular">
                      {line.amount > 0 ? formatMoney(line.amount) : ""}
                    </td>
                    <td className="py-2 text-right tabular text-ink-2">
                      {formatMoney(Math.abs(line.balance))}
                      <span className="ml-1 text-xs text-ink-3">
                        {line.balance < 0 ? "Dr" : "Cr"}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Scroller>
        )}
      </Card>
    </div>
  );
}
